import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { Archive, Copy, Download, Ellipsis, Info, Layers, Pencil, Video } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useGetJobDetails } from "../../../api/generated/endpoints/generation/generation";
import type { GenerationOutput, Job } from "../../../api/generated/model";
import { DetailCard } from "../../../ui/DetailModal";
import { formatDate } from "../../../ui/format";
import { MediaFailed, MediaLoading } from "../../../ui/MediaCard";
import { Soon } from "../../../ui/Soon";
import { detailTitle } from "../../../platform/studio/draft";
import classes from "./GenerationDetail.module.css";
import { statusLook } from "./job-status";
import { previewKind } from "../../../platform/media/output-kind";
import { OutputMedia } from "../../../platform/media/OutputMedia";
import { outputAlt, outputMedium } from "./outputs";
import { creditsText } from "../../../ui/credits";
import { useDownloadOutput } from "../../../platform/jobs/download-output";
import { useSaveOutput } from "../../../platform/jobs/SaveOutput";
import { failedWords } from "../../../platform/jobs/failed-words";
import { shownOutputs } from "../../../platform/jobs/output-picks";
import { TYPE_NAMES } from "../../../platform/jobs/type-names";

function outputText(output: GenerationOutput): string {
  if (output.status === "pending") return "Waiting";
  if (output.status === "failed") return output.error?.message ?? "Failed";
  const parts = [output.mime ?? "File"];
  if (output.width !== null && output.height !== null) {
    parts.push(`${output.width} by ${output.height}`);
  }
  if (output.duration_ms !== null) parts.push(`${Math.round(output.duration_ms / 1000)} s`);
  return parts.join(", ");
}

/**
 * The facts under the prompt, as pills: what was asked for, what it cost, when. The cost is what
 * the job was charged once it is (a template's run: its steps', #623), else its quote.
 */
function factsOf(job: Job, charged: number | null | undefined): string[] {
  const params = job.params;
  const duration = params?.duration_seconds;
  return [
    job.model,
    params?.aspect_ratio,
    params?.resolution,
    duration === null || duration === undefined ? null : `${duration} s`,
    charged === null || charged === undefined
      ? creditsText(job.quote)
      : `${charged} ${charged === 1 ? "credit" : "credits"}`,
    `Started ${formatDate(job.created_at)}`,
  ].filter((fact): fact is string => fact !== null && fact !== undefined && fact !== "");
}

/** The media pane: the output on show, its placeholder while it is made, or what went wrong. */
interface OutputPaneProps {
  job: Job;
  output: GenerationOutput | undefined;
  readAgain: () => void;
}

function OutputPane(props: OutputPaneProps) {
  const { job, output, readAgain } = props;
  const ratio = job.params?.aspect_ratio ?? null;
  if (job.error !== null && output?.status !== "delivered") {
    return <MediaFailed {...failedWords(job.type, job.error.message)} medium={outputMedium(job)} />;
  }
  if (output === undefined || output.status === "pending") {
    const label = `Output ${(output?.index ?? 0) + 1}, waiting`;
    // Live's loader while it is made (spec ui-perf/ui-switch-classic-simple-pro, decision 19).
    return <MediaLoading aspectRatio={ratio} label={label} medium={outputMedium(job)} />;
  }
  if (output.status === "failed") return <MediaFailed title="This output failed" medium={outputMedium(job)} />;
  const kind = previewKind(output);
  if (output.url === null || kind === null) return <Text>{outputText(output)}</Text>;
  const alt = outputAlt(job, output);
  return <OutputMedia kind={kind} src={output.url} alt={alt} onExpired={readAgain} />;
}

/** Above the card: keep the output on show in the library, or start from it. */
interface OutputActionsProps {
  teamId: string;
  job: Job;
  output: GenerationOutput;
  /** Reads the job again, for fresh output URLs. */
  readAgain: () => void;
}

function OutputActions({ teamId, job, output, readAgain }: OutputActionsProps) {
  if (output.status !== "delivered" || job.brand_id === null) return null;
  // Keyed by the output, so a save's result belongs to the output on show.
  return (
    <DeliveredActions
      key={output.index}
      teamId={teamId}
      brandId={job.brand_id}
      job={job}
      output={output}
      readAgain={readAgain}
    />
  );
}

/**
 * Live's row of white icons above the card (design-system.md 3.4, G1 decisions 18 and 19):
 * download saves the file; layers and info are Coming soon; "More" holds the rest of what we do:
 * save to the library and, for an image, change it or make a video of it. A save's or a
 * download's result shows beside the row.
 */
function DeliveredActions(props: OutputActionsProps & { brandId: string }) {
  const { teamId, brandId, job, output, readAgain } = props;
  const index = output.index;
  const saving = useSaveOutput({ teamId, brandId, jobId: job.id, index });
  const download = useDownloadOutput(job, output, readAgain);
  const image = previewKind(output) === "image";
  const soon = (label: string, icon: ReactNode) => (
    <Soon>
      {(soonProps) => (
        <ActionIcon
          {...soonProps}
          variant="transparent"
          aria-label={label}
          className={classes.action}
        >
          {icon}
        </ActionIcon>
      )}
    </Soon>
  );
  const from = (type: "image.tweak" | "video.image", label: string, icon: ReactNode) => (
    <Menu.Item
      leftSection={icon}
      renderRoot={(rootProps) => (
        <Link
          {...rootProps}
          to="/teams/$teamId/brands/$brandId/generate"
          params={{ teamId, brandId }}
          search={{ type, job: job.id, output: index }}
        />
      )}
    >
      {label}
    </Menu.Item>
  );
  return (
    <>
      {saving.result}
      {download.result}
      <Tooltip label="Download">
        <ActionIcon
          variant="transparent"
          aria-label={`Download output ${index + 1}`}
          className={classes.action}
          loading={download.busy}
          onClick={download.start}
        >
          <Download size={20} />
        </ActionIcon>
      </Tooltip>
      {soon("Layers", <Layers size={20} />)}
      {soon("Info", <Info size={20} />)}
      <Menu position="bottom-end" withinPortal={false}>
        <Menu.Target>
          <ActionIcon
            variant="transparent"
            aria-label={`More actions: output ${index + 1}`}
            className={classes.action}
          >
            <Ellipsis size={20} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<Archive size={16} />}
            disabled={saving.saving || saving.saved}
            onClick={saving.save}
          >
            Save to library
          </Menu.Item>
          {image ? from("image.tweak", "Change this image", <Pencil size={16} />) : null}
          {image ? from("video.image", "Make a video of it", <Video size={16} />) : null}
        </Menu.Dropdown>
      </Menu>
    </>
  );
}

/** Opens the studio on the job's medium with its draft filled from this job (`?clone=`). */
function CloneButton({ teamId, brandId, job }: { teamId: string; brandId: string; job: Job }) {
  const medium = job.type.startsWith("video") ? { medium: "video" as const } : {};
  return (
    <Button
      className={classes.clone}
      leftSection={<Copy size={16} aria-hidden />}
      renderRoot={(rootProps) => (
        <Link
          {...rootProps}
          to="/teams/$teamId/brands/$brandId/generate"
          params={{ teamId, brandId }}
          search={{ ...medium, clone: job.id }}
        />
      )}
    >
      Clone
    </Button>
  );
}

export interface GenerationDetailProps {
  teamId: string;
  job: Job;
  /** Reads the job again, for fresh output URLs. */
  readAgain: () => void;
  /** 1 on the job's page, 2 inside the modal over another page. */
  headingOrder: 1 | 2;
}

/**
 * One job's detail (design-system.md 3.4): its outputs in the media pane, one at a time, and on
 * the right the type, the status, "Clone", the prompt, the facts and every output's state. The
 * job's page and the studio's modal render it the same.
 */
export function GenerationDetail({ teamId, job, readAgain, headingOrder }: GenerationDetailProps) {
  const [shown, setShown] = useState(0);
  const charged = useGetJobDetails(teamId, job.id).data?.charged_credits;
  // A Layerize's unused outputs are no outputs to show (G13).
  const outputs = shownOutputs(job);
  const count = outputs.length;
  const at = Math.min(shown, Math.max(count - 1, 0));
  const output = outputs[at];
  const move = (step: number) => setShown((at + step + count) % count);
  const status = statusLook(job.status);
  return (
    <DetailCard
      media={<OutputPane job={job} output={output} readAgain={readAgain} />}
      actions={
        output === undefined ? undefined : (
          <OutputActions teamId={teamId} job={job} output={output} readAgain={readAgain} />
        )
      }
      carousel={count > 1 ? { onPrevious: () => move(-1), onNext: () => move(1) } : undefined}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group gap="sm">
          <Title order={headingOrder} className={classes.title}>
            {detailTitle(job.type)}
          </Title>
          {/* An <output> is a status region, so a change of status is read out. */}
          <Badge
            component="output"
            color={status.color}
            variant="light"
            aria-label={`Status: ${status.label}`}
          >
            {status.label}
          </Badge>
        </Group>
        {/* A type the Studio cannot start again (a template's run) offers no Clone. */}
        {job.brand_id === null || !TYPE_NAMES[job.type].clone ? null : (
          <CloneButton teamId={teamId} brandId={job.brand_id} job={job} />
        )}
      </Group>
      <div>
        <Text className={classes.label}>Prompt</Text>
        <Text className={classes.prompt}>{job.params?.prompt ?? "No longer kept"}</Text>
      </div>
      <Group gap={6}>
        {factsOf(job, charged).map((fact) => (
          <span key={fact} className={classes.fact}>
            {fact}
          </span>
        ))}
      </Group>
      {count === 0 ? null : (
        <div>
          <Text className={classes.label}>Outputs</Text>
          {outputs.map((each, index) => (
            <UnstyledButton
              key={each.index}
              className={classes.output}
              aria-pressed={index === at}
              onClick={() => setShown(index)}
            >
              <span className={classes.outputName}>Output {each.index + 1}</span>
              <span>{outputText(each)}</span>
            </UnstyledButton>
          ))}
        </div>
      )}
    </DetailCard>
  );
}
